'use client';

import React, { useState } from 'react';
import { Star, X, ChevronDown, ChevronRight } from 'lucide-react';
import type { FilterState } from './types';
import { TRADE_SUPER_CATEGORIES } from '@/app/admin/scraper/data/tradeCategories';

interface Props {
  filters: FilterState;
  updateFilter: <K extends keyof FilterState>(key: K, value: FilterState[K]) => void;
}

const RATING_OPTIONS = [
  { value: 0, label: 'Any rating' },
  { value: 4, label: '4+ stars' },
  { value: 4.5, label: '4.5+ stars' },
  { value: 4.8, label: '4.8+ stars' },
];

const TIER_OPTIONS = [
  { value: 'all', label: 'All tiers' },
  { value: 'premium', label: 'Premium' },
  { value: 'pro', label: 'Pro' },
  { value: 'free', label: 'Free' },
];

const RADIUS_VALUES = [2, 5, 10, 15, 25, 50];

export default function DirectoryFilters({ filters, updateFilter }: Props) {
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);

  const hasActiveFilters =
    filters.category !== 'all' ||
    filters.minRating > 0 ||
    filters.tier !== 'all' ||
    filters.availableNow ||
    filters.radius !== 10;

  const resetAll = () => {
    updateFilter('category', 'all');
    updateFilter('minRating', 0);
    updateFilter('tier', 'all');
    updateFilter('availableNow', false);
    updateFilter('radius', 10);
    setExpandedCategory(null);
  };

  const handleParentCatClick = (parentCatId: string) => {
    // Toggle expand/collapse
    setExpandedCategory((prev) => (prev === parentCatId ? null : parentCatId));
    // Also set the category filter to this parent
    updateFilter('category', parentCatId);
  };

  const handleServiceClick = (serviceValue: string) => {
    updateFilter('category', serviceValue);
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-card p-4 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="font-display font-700 text-gray-900 text-sm">Filters</h3>
        {hasActiveFilters && (
          <button
            onClick={resetAll}
            className="flex items-center gap-1 text-xs text-plum-600 hover:text-plum-800 font-medium transition-colors"
          >
            <X size={12} />
            Clear all
          </button>
        )}
      </div>

      {/* Trade category */}
      <div>
        <p className="text-xs font-600 text-gray-500 uppercase tracking-wider mb-2">Trade category</p>
        <div className="space-y-0.5">
          {/* All trades option */}
          <button
            onClick={() => {
              updateFilter('category', 'all');
              setExpandedCategory(null);
            }}
            className={`w-full text-left px-3 py-1.5 rounded-lg text-sm transition-colors ${
              filters.category === 'all' ?'bg-plum-50 text-plum-800 font-semibold' :'text-gray-600 hover:bg-gray-50'
            }`}
          >
            All trades
          </button>

          {/* Grouped by super-category */}
          {TRADE_SUPER_CATEGORIES.map((superCat) => (
            <div key={superCat.id} className="pt-2">
              <p className="px-3 py-1 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                {superCat.emoji} {superCat.label}
              </p>
              {superCat.categories.map((parentCat) => {
                const isExpanded = expandedCategory === parentCat.id;
                const isParentActive = filters.category === parentCat.id;
                const isChildActive = parentCat.services.some(
                  (s) => s.value === filters.category
                );

                return (
                  <div key={`cat-filter-${parentCat.id}`}>
                    {/* Parent category row */}
                    <button
                      onClick={() => handleParentCatClick(parentCat.id)}
                      className={`w-full text-left px-3 py-1.5 rounded-lg text-sm transition-colors flex items-center justify-between group ${
                        isParentActive || isChildActive
                          ? 'bg-plum-50 text-plum-800 font-semibold' :'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <span>
                        {parentCat.label}
                        <span className="ml-1 text-xs text-gray-400">
                          ({parentCat.services.length})
                        </span>
                      </span>
                      {isExpanded ? (
                        <ChevronDown size={13} className="flex-shrink-0 text-gray-400" />
                      ) : (
                        <ChevronRight size={13} className="flex-shrink-0 text-gray-300 group-hover:text-gray-400" />
                      )}
                    </button>

                    {/* Subcategory services — shown when expanded */}
                    {isExpanded && (
                      <div className="ml-3 mt-0.5 mb-1 border-l-2 border-plum-100 pl-2 space-y-0.5">
                        {parentCat.services.map((service) => (
                          <button
                            key={`service-${service.value}`}
                            onClick={() => handleServiceClick(service.value)}
                            className={`w-full text-left px-2.5 py-1 rounded-md text-xs transition-colors ${
                              filters.category === service.value
                                ? 'bg-plum-100 text-plum-900 font-semibold' :'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                            }`}
                          >
                            {service.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Radius */}
      <div>
        <p className="text-xs font-600 text-gray-500 uppercase tracking-wider mb-2">
          Radius — <span className="text-gray-800 font-semibold">{filters.radius} miles</span>
        </p>
        <div className="flex flex-wrap gap-1.5">
          {RADIUS_VALUES.map((r) => (
            <button
              key={`radius-${r}`}
              onClick={() => updateFilter('radius', r)}
              className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
                filters.radius === r
                  ? 'bg-plum-800 text-white' :'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {r}mi
            </button>
          ))}
        </div>
      </div>

      {/* Min rating */}
      <div>
        <p className="text-xs font-600 text-gray-500 uppercase tracking-wider mb-2">Minimum rating</p>
        <div className="space-y-1">
          {RATING_OPTIONS.map((opt) => (
            <button
              key={`rating-${opt.value}`}
              onClick={() => updateFilter('minRating', opt.value)}
              className={`w-full text-left px-3 py-1.5 rounded-lg text-sm flex items-center gap-1.5 transition-colors ${
                filters.minRating === opt.value
                  ? 'bg-amber-50 text-amber-800 font-semibold' :'text-gray-600 hover:bg-gray-50'
              }`}
            >
              {opt.value > 0 && <Star size={12} className="text-amber-400 fill-amber-400" />}
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tier */}
      <div>
        <p className="text-xs font-600 text-gray-500 uppercase tracking-wider mb-2">Subscription tier</p>
        <div className="space-y-1">
          {TIER_OPTIONS.map((opt) => (
            <button
              key={`tier-filter-${opt.value}`}
              onClick={() => updateFilter('tier', opt.value)}
              className={`w-full text-left px-3 py-1.5 rounded-lg text-sm transition-colors ${
                filters.tier === opt.value
                  ? 'bg-plum-50 text-plum-800 font-semibold' :'text-gray-600 hover:bg-gray-50'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Available now */}
      <div>
        <p className="text-xs font-600 text-gray-500 uppercase tracking-wider mb-2">Availability</p>
        <label className="flex items-center gap-2.5 px-3 py-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
          <div
            onClick={() => updateFilter('availableNow', !filters.availableNow)}
            className={`relative w-9 h-5 rounded-full transition-colors duration-200 flex-shrink-0 cursor-pointer ${
              filters.availableNow ? 'bg-mint-500' : 'bg-gray-200'
            }`}
          >
            <div
              className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform duration-200 ${
                filters.availableNow ? 'translate-x-4' : 'translate-x-0.5'
              }`}
            />
          </div>
          <span className="text-sm text-gray-700">Available now only</span>
        </label>
      </div>
    </div>
  );
}